// Calendar API — typed-обёртки над 9 endpoints /api/calendar/events/*.

import { apiClient } from './client';
import type { Page } from './tasks';

/** Состояние события. */
export type CalendarState = 'SCHEDULED' | 'CANCELED' | 'COMPLETED';

/** Тип события. */
export type CalendarType = 'MEETING' | 'CALL' | 'TASK' | 'REMINDER';

/** Статус ответа участника. */
export type AttendeeStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE';

/** DTO события. */
export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  state: CalendarState;
  type: CalendarType;
  organizerUserId: string;
  departmentId: string;
  /** FK → deals.id (nullable; связь события со сделкой CRM, R20). */
  dealId: string | null;
  /** FK → contacts.id (nullable; связь события с контактом CRM, R20). */
  contactId: string | null;
  /** FK → companies.id (nullable; связь события с компанией CRM, R20). */
  companyId: string | null;
  /** Флаг «весь день» (R20; backend отдаёт в wire-формате, нужен для CSV-экспорта R22). */
  allDay: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** DTO участника события. */
export interface EventAttendee {
  id: string;
  eventId: string;
  userId: string;
  status: AttendeeStatus;
  respondedAt: string | null;
}

export interface CalendarFilters {
  page?: number;
  size?: number;
  from?: string;
  to?: string;
  type?: CalendarType;
  departmentId?: string;
  /** Фильтр по сделке (UUID, R20). */
  dealId?: string;
  /** Фильтр по контакту (UUID, R20). */
  contactId?: string;
  /** Фильтр по компании (UUID, R20). */
  companyId?: string;
  /** Живой поиск: title события ИЛИ название связанной сделки (ILIKE, R20). */
  q?: string;
}

export interface CalendarCreateRequest {
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  /** Флаг «весь день» (R20; quick-action «след. контакт» шлёт false). */
  allDay?: boolean;
  type: CalendarType;
  departmentId: string;
  /** FK → deals.id (optional; привязка события к сделке CRM, R20). */
  dealId?: string;
  /** Организатор (optional; backend по умолчанию берёт текущего пользователя из токена). */
  organizerUserId?: string;
}

export interface CalendarUpdateRequest {
  title?: string;
  description?: string | null;
  location?: string | null;
  startAt?: string;
  endAt?: string;
  type?: CalendarType;
  expectedUpdatedAt: string;
}

export interface CalendarAddAttendeeRequest {
  userId: string;
}

export interface CalendarRespondAttendeeRequest {
  status: AttendeeStatus;
}

/** Calendar API. */
export const calendarApi = {
  /** GET /api/calendar/events?filters → Page<CalendarEvent>. */
  async list(filters: CalendarFilters = {}): Promise<Page<CalendarEvent>> {
    const { data } = await apiClient.get<Page<CalendarEvent>>('/api/calendar/events', { params: filters });
    return data;
  },

  /** GET /api/calendar/events/{id} → CalendarEvent. */
  async get(id: string): Promise<CalendarEvent> {
    const { data } = await apiClient.get<CalendarEvent>(`/api/calendar/events/${id}`);
    return data;
  },

  /** POST /api/calendar/events → CalendarEvent. */
  async create(payload: CalendarCreateRequest): Promise<CalendarEvent> {
    const { data } = await apiClient.post<CalendarEvent>('/api/calendar/events', payload);
    return data;
  },

  /** PUT /api/calendar/events/{id} → CalendarEvent. */
  async update(id: string, payload: CalendarUpdateRequest): Promise<CalendarEvent> {
    const { data } = await apiClient.put<CalendarEvent>(`/api/calendar/events/${id}`, payload);
    return data;
  },

  /** DELETE /api/calendar/events/{id}. */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/calendar/events/${id}`);
  },

  /** POST /api/calendar/events/{id}/attendees → EventAttendee. */
  async addAttendee(eventId: string, payload: CalendarAddAttendeeRequest): Promise<EventAttendee> {
    const { data } = await apiClient.post<EventAttendee>(
      `/api/calendar/events/${eventId}/attendees`,
      payload,
    );
    return data;
  },

  /** DELETE /api/calendar/events/{id}/attendees/{userId}. */
  async removeAttendee(eventId: string, userId: string): Promise<void> {
    await apiClient.delete(`/api/calendar/events/${eventId}/attendees/${userId}`);
  },

  /** PUT /api/calendar/events/{id}/attendees/{userId} → EventAttendee. */
  async respondAttendee(
    eventId: string,
    userId: string,
    payload: CalendarRespondAttendeeRequest,
  ): Promise<EventAttendee> {
    const { data } = await apiClient.put<EventAttendee>(
      `/api/calendar/events/${eventId}/attendees/${userId}`,
      payload,
    );
    return data;
  },

  /** GET /api/calendar/events/{id}/attendees → EventAttendee[]. */
  async listAttendees(eventId: string): Promise<EventAttendee[]> {
    const { data } = await apiClient.get<EventAttendee[]>(`/api/calendar/events/${eventId}/attendees`);
    return data;
  },
};
